import { HsaEligibility } from "@redotech/redo-conversion-hsa-common/hsa-eligibility";
import {
  GALE_PAYMENTS_ELIGIBILITY_METAFIELD_KEY,
  GALE_PAYMENTS_METAFIELD_NAMESPACE,
  GALE_PAYMENTS_PRODUCT_TYPE_METAFIELD_KEY,
  REDO_HSA_ELIGIBILITY_METAFIELD_KEY,
  REDO_HSA_METAFIELD_NAMESPACE,
  resolveHsaEligibilityFromMetafields,
} from "@redotech/redo-conversion-hsa-common/hsa-metafields";
import { useEffect, useRef, useState } from "react";
import { StorefrontClient } from "../../client/storefront";
import { getVariantIdFromUrl } from "../../utils";
import { isUnitedStates } from "./hsa-country-gate";

const METAFIELD_IDENTIFIERS = [
  {
    namespace: REDO_HSA_METAFIELD_NAMESPACE,
    key: REDO_HSA_ELIGIBILITY_METAFIELD_KEY,
  },
  {
    namespace: GALE_PAYMENTS_METAFIELD_NAMESPACE,
    key: GALE_PAYMENTS_ELIGIBILITY_METAFIELD_KEY,
  },
  {
    namespace: GALE_PAYMENTS_METAFIELD_NAMESPACE,
    key: GALE_PAYMENTS_PRODUCT_TYPE_METAFIELD_KEY,
  },
];

const ELIGIBLE_VARIANTS_STORAGE_KEY = "redoHsaEligibleVariants";

function getSelectedVariantId(): string | null {
  const control = document.querySelector<HTMLSelectElement | HTMLInputElement>(
    "select[name='id'], input[name='id']",
  );
  return control?.value || getVariantIdFromUrl(location.href);
}

function rememberEligibleVariant(
  variantId: string,
  eligibility: HsaEligibility,
): void {
  try {
    const raw = localStorage.getItem(ELIGIBLE_VARIANTS_STORAGE_KEY);
    const list: { id: string; eligibility: string }[] = raw
      ? JSON.parse(raw)
      : [];
    const next = list.filter((v) => String(v.id) !== String(variantId));
    if (eligibility !== HsaEligibility.NOT_ELIGIBLE) {
      next.push({ id: variantId, eligibility });
    }
    localStorage.setItem(ELIGIBLE_VARIANTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage may be unavailable (e.g. private mode); ignore.
  }
}

export interface HsaVariantEligibility {
  eligibility: HsaEligibility;
  /** False when the selling-plan lookup fails: the walkthrough then omits the
   * subscription tab rather than hiding the badge. */
  offersSubscription: boolean;
}

/**
 * Resolves HSA/FSA eligibility for the selected PDP variant, reacting to variant
 * switches and section reloads. Undefined off-US / while loading / on error.
 */
export function useHsaVariantEligibility({
  storefrontClient,
}: {
  storefrontClient: StorefrontClient;
}): HsaVariantEligibility | undefined {
  const [variantId, setVariantId] = useState<string | null>(() =>
    getSelectedVariantId(),
  );
  const [eligibility, setEligibility] = useState<
    HsaVariantEligibility | undefined
  >(undefined);
  const cacheRef = useRef(new Map<string, HsaVariantEligibility>());

  useEffect(() => {
    const onChange = (event: Event) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const target = event.target as HTMLElement | null;
      if (target?.matches?.("select[name='id'], input[name='id']")) {
        setVariantId(getSelectedVariantId());
      }
    };
    const onSectionLoad = () => setVariantId(getSelectedVariantId());
    document.addEventListener("change", onChange);
    document.addEventListener("shopify:section:load", onSectionLoad);
    return () => {
      document.removeEventListener("change", onChange);
      document.removeEventListener("shopify:section:load", onSectionLoad);
    };
  }, []);

  useEffect(() => {
    if (!variantId || !isUnitedStates()) {
      setEligibility(undefined);
      return;
    }

    const cached = cacheRef.current.get(variantId);
    if (cached !== undefined) {
      setEligibility(cached);
      return;
    }

    const controller = new AbortController();
    void (async () => {
      try {
        const [metafields, offersSubscription] = await Promise.all([
          storefrontClient.getVariantMetafields(
            variantId,
            METAFIELD_IDENTIFIERS,
            controller.signal,
          ),
          storefrontClient
            .getVariantHasRecurringSellingPlan(variantId, controller.signal)
            .catch(() => false),
        ]);
        if (controller.signal.aborted) {
          return;
        }
        const valueOf = (namespace: string, key: string) =>
          metafields?.find((mf) => mf.namespace === namespace && mf.key === key)
            ?.value ?? null;
        const resolved = resolveHsaEligibilityFromMetafields({
          redoValue: valueOf(
            REDO_HSA_METAFIELD_NAMESPACE,
            REDO_HSA_ELIGIBILITY_METAFIELD_KEY,
          ),
          galeEligibilityValue: valueOf(
            GALE_PAYMENTS_METAFIELD_NAMESPACE,
            GALE_PAYMENTS_ELIGIBILITY_METAFIELD_KEY,
          ),
          galeProductTypeValue: valueOf(
            GALE_PAYMENTS_METAFIELD_NAMESPACE,
            GALE_PAYMENTS_PRODUCT_TYPE_METAFIELD_KEY,
          ),
        });
        const result = { eligibility: resolved, offersSubscription };
        cacheRef.current.set(variantId, result);
        rememberEligibleVariant(variantId, resolved);
        setEligibility(result);
      } catch {
        if (!controller.signal.aborted) {
          setEligibility(undefined);
        }
      }
    })();

    return () => controller.abort();
  }, [variantId, storefrontClient]);

  return eligibility;
}
